import {GetServerSideProps, GetServerSidePropsContext, NextApiRequest} from 'next';
import React from 'react';

import {RENDER_MODES, mergeWithArrayOverwrite} from '@udemy/nextjs-core';
import {
    fetchCertificationBundlesForTopic,
    CertificationBundle,
} from '@udemy/react-certification-preparation';
import {udSentry} from '@udemy/sentry';

import {i18nPathToBundleMap} from 'next.config.i18n';
import {
    VoucherLandingPageFeature,
    VoucherLandingPageFeatureProps,
} from 'src/features/voucher-landing-page';
import {getCompactTranslations} from 'src/lib-server/locale.server';
import {getPassThroughHeaders} from 'src/lib-server/request-headers';
import {fetcher} from 'src/lib/gql-fetcher';
import {AppContextData} from 'src/types/page-props';

import {getLayout} from '../../components/layout';
import {getStaticAppContext, getUserSpecificAppContext} from '../api/context.api';
import {getCoursesToGetYouStartedUnit} from './lib/get-courses-to-get-you-started-unit';
import {getVoucherData} from './lib/get-voucher-landing-page-data';

export const getServerSideProps: GetServerSideProps = async (
    context: GetServerSidePropsContext,
) => {
    const {locale, params, req, resolvedUrl} = context;
    const slug = params?.slug;
    const localeStr = locale as string;
    const translations = await getCompactTranslations(localeStr);
    const [staticData, userData] = await Promise.all([
        getStaticAppContext({locale: localeStr}),
        getUserSpecificAppContext({locale: localeStr}, req as NextApiRequest),
    ]);
    const fullLocale = i18nPathToBundleMap[locale as keyof typeof i18nPathToBundleMap]
        .split('_')
        .join('-');

    const passThroughHeaders = getPassThroughHeaders(req.headers);

    let voucherData = null;
    try {
        voucherData = await getVoucherData(slug as string, localeStr, passThroughHeaders);
    } catch (error) {
        udSentry.captureException(error);
    }

    // If voucherData is null, return 404
    if (!voucherData) {
        return {
            notFound: true,
        };
    }

    let coursesToGetYouStartedUnit = null;
    const topicId = voucherData.exam.topic?.id;
    if (topicId && staticData.udData) {
        try {
            coursesToGetYouStartedUnit = await getCoursesToGetYouStartedUnit(
                topicId,
                staticData.udData,
            );
        } catch (error) {
            udSentry.captureException(error);
        }
    }

    let bundles: CertificationBundle[] = [];
    try {
        if (topicId) {
            bundles = await fetchCertificationBundlesForTopic(topicId, {
                customGqlFetcher: fetcher,
                locale: fullLocale,
            });
        }
    } catch (error) {
        udSentry.captureException(error);
    }

    voucherData = {
        ...voucherData,
        coursesToGetYouStartedUnit,
        certificationBundles: bundles,
    };

    const appContext: AppContextData = {
        ...mergeWithArrayOverwrite(staticData, userData),
    };

    return {
        props: {
            appContext,
            locale: localeStr,
            pageKey: 'voucher_page',
            renderMode: RENDER_MODES.SSR,
            resolvedUrl,
            translations,
            voucherData,
        },
    };
};

const IndexPage = (props: VoucherLandingPageFeatureProps) => {
    return <VoucherLandingPageFeature {...props} />;
};

IndexPage.getLayout = getLayout;
export default IndexPage;
